/**
 * OSS 图片缩放模式。
 *
 * - fixed: 固定宽高，强制缩放到指定尺寸
 * - proportional: 按百分比缩放
 * - lfit: 等比缩放，限制在指定宽高内，默认推荐模式
 * - mfit: 等比缩放，至少覆盖指定宽高
 * - pad: 固定宽高，不足区域用颜色填充
 * - fill: 固定宽高，必要时裁剪
 */
export type ImageResizeMode = "fixed" | "proportional" | "lfit" | "mfit" | "pad" | "fill";

/**
 * SmartImage URL 处理选项。
 */
export interface ImageUrlOpts {
    /**
     * 是否禁用 OSS 缩放。
     * 为 true 时，只返回完整图片地址，不拼接任何 x-oss-process 参数。
     */
    noResize?: boolean;
    /**
     * 缩放模式，默认值为 lfit。
     */
    mode?: ImageResizeMode;
    /**
     * 目标宽度，单位 px。
     */
    width?: number;
    /**
     * 目标高度，单位 px。
     */
    height?: number;
    /**
     * 百分比缩放，取值范围 1-1000，仅 proportional 模式生效。
     */
    percent?: number;
    /**
     * 是否限制放大。
     * OSS 中 limit_1 表示目标图大于原图时不放大。
     */
    limit?: number;
    /**
     * pad 模式下的填充色，例如 FF0000。
     */
    color?: string;
    /**
     * 默认 CDN / OSS 前缀。
     */
    cdnPrefix?: string;
    /**
     * yesorno 资源专用 CDN / OSS 前缀。
     * 当路径包含 /yesorno/ 时优先使用该前缀。
     */
    ynCdnPrefix?: string;
}

/**
 * OSS 缩放默认参数。
 *
 * 说明：
 * - 默认使用 lfit，避免大多数业务图被拉伸
 * - 默认 limit=1，避免把小图强行放大
 * - 默认 percent=70，仅作为 proportional 模式的兜底值
 */
const DEFAULT_IMAGE_OPTIONS: Required<Pick<ImageUrlOpts, "mode" | "limit" | "percent">> = {
    mode: "lfit",
    limit: 1,
    percent: 70,
};

/**
 * 当前允许附加 OSS 缩放参数的图片后缀。
 * 非这些类型会直接返回原始 URL，避免对视频、文档等资源误加处理参数。
 */
const SUPPORTED_EXTENSIONS = new Set( [
    ".jpg",
    ".jpeg",
    ".png",
    ".bmp",
    ".gif",
    ".webp",
    ".tiff",
    ".tif",
    ".heic",
] );

const ABSOLUTE_URL_RE = /^(?:https?:)?\/\//i;
const SPECIAL_SCHEME_RE = /^(?:data|blob):/i;

/**
 * 安全拼接前缀和资源路径，避免出现双斜杠或缺少斜杠的情况。
 */
const normalizeJoinUrl = ( prefix: string, path: string ) => {
    if ( !prefix ) {
        return path;
    }

    return `${prefix.replace( /\/$/, "" )}/${path.replace( /^\//, "" )}`;
};

/**
 * 从路径中提取文件后缀。
 *
 * 这里会先剥离 query 和 hash，确保：
 * - /a/b/c.jpg?x=1     => .jpg
 * - /a/b/c.webp#hash   => .webp
 */
const getFileExtension = ( path: string ) => {
    const pathWithoutHash = path.split( "#" )[0] ?? "";
    const pathWithoutQuery = pathWithoutHash.split( "?" )[0] ?? "";
    const lastDotIndex = pathWithoutQuery.lastIndexOf( "." );

    if ( lastDotIndex === -1 ) {
        return "";
    }

    return pathWithoutQuery.slice( lastDotIndex ).toLowerCase();
};

/**
 * 判断当前资源是否属于支持 OSS 缩放的图片类型。
 */
const isResizableImage = ( path: string ) => {
    const fileExt = getFileExtension( path );
    return SUPPORTED_EXTENSIONS.has( fileExt );
};

/**
 * 判断当前图片是否属于项目自己的 OSS CDN 域名。
 *
 * 这里故意只认 NEXT_PUBLIC_OSS_CDN_URL：
 * - 命中该域名时，说明图片走的是项目自有 OSS 图片链路，可以安全追加 x-oss-process
 * - 其它第三方图片域名即使偶尔兼容该参数，也不作为通用规则处理，避免出现同域名不同资源行为不一致的问题
 */
const isProjectOssUrl = ( fullUrl: string, options: Pick<ImageUrlOpts, "cdnPrefix"> = {} ) => {
    const cdnPrefix = options.cdnPrefix || process.env.NEXT_PUBLIC_OSS_CDN_URL || "";
    const normalizedCdnPrefix = cdnPrefix.replace( /\/$/, "" );

    if ( !normalizedCdnPrefix ) {
        return false;
    }

    return fullUrl.startsWith( normalizedCdnPrefix );
};

/**
 * 将传入的图片路径转换为可访问的完整 URL。
 *
 * 规则：
 * 1. 空字符串直接返回空
 * 2. http / https / // / data: / blob: 直接原样返回
 * 3. 相对路径自动补 CDN 前缀
 * 4. 路径包含 /yesorno/ 时，优先使用 yesorno 专属前缀
 */
const getBaseImageUrl = ( path: string, options: Pick<ImageUrlOpts, "cdnPrefix" | "ynCdnPrefix"> = {} ) => {
    const trimmed = path.trim();
    if ( !trimmed ) {
        return "";
    }

    if ( ABSOLUTE_URL_RE.test( trimmed ) || SPECIAL_SCHEME_RE.test( trimmed ) ) {
        return trimmed;
    }

    const defaultCdnPrefix = options.cdnPrefix || process.env.NEXT_PUBLIC_OSS_CDN_URL || "";
    const yesornoCdnPrefix = options.ynCdnPrefix || process.env.NEXT_PUBLIC_OSS_YN_CDN_URL || defaultCdnPrefix;
    const prefix = /\/yesorno\//.test( trimmed ) ? yesornoCdnPrefix : defaultCdnPrefix;

    return normalizeJoinUrl( prefix, trimmed );
};

/**
 * 根据传入选项生成 OSS 缩放参数字符串。
 *
 * 只有在“确实存在有效缩放条件”时才返回参数，否则返回空字符串。
 * 这样可以保证老调用方在没有提供尺寸时，不会无意中被附加处理参数。
 */
const buildResizeParams = ( options: ImageUrlOpts ) => {
    const opts = { ...DEFAULT_IMAGE_OPTIONS, ...options };

    if ( opts.noResize ) {
        return "";
    }

    let processParams = "x-oss-process=image/resize";

    if ( opts.mode === "proportional" ) {
        if ( !opts.percent ) {
            return "";
        }

        const validPercent = Math.max( 1, Math.min( 1000, Math.round( opts.percent ) ) );
        processParams += `,p_${validPercent}`;
        return processParams;
    }

    if ( opts.mode === "fixed" ) {
        if ( !opts.width || !opts.height ) {
            return "";
        }

        processParams += `,m_fixed,w_${opts.width},h_${opts.height}`;
        return processParams;
    }

    if ( !opts.width && !opts.height ) {
        return "";
    }

    processParams += `,m_${opts.mode}`;

    if ( opts.width ) {
        processParams += `,w_${opts.width}`;
    }

    if ( opts.height ) {
        processParams += `,h_${opts.height}`;
    }

    processParams += `,limit_${opts.limit}`;

    if ( opts.mode === "pad" && opts.color ) {
        processParams += `,color_${opts.color}`;
    }

    return processParams;
};

/**
 * 将 OSS 处理参数追加到 URL。
 *
 * 兼容点：
 * - 如果 URL 已经包含 x-oss-process，则直接返回，避免重复拼接
 * - 如果 URL 已包含 query，则使用 &
 * - 如果 URL 带 hash，则保持 hash 在最后
 */
const appendResizeParams = ( url: string, processParams: string ) => {
    if ( !processParams || url.includes( "x-oss-process=" ) ) {
        return url;
    }

    const [ urlWithoutHash, hash = "" ] = url.split( "#" );
    const joiner = urlWithoutHash.includes( "?" ) ? "&" : "?";
    return `${urlWithoutHash}${joiner}${processParams}${hash ? `#${hash}` : ""}`;
};

/**
 * 将原始图片路径处理为最终可渲染的图片 URL。
 *
 * 完整流程：
 * 1. 先将相对路径转换成完整 CDN 地址
 * 2. 如果禁用缩放，直接返回
 * 3. 如果不是支持的图片后缀，直接返回
 * 4. 根据模式、宽高、percent 等参数生成 OSS 处理串
 * 5. 把处理参数拼接到 URL 上
 *
 * 该函数是纯 URL 层逻辑，不关心 React 渲染、fallback、onError 等视图行为。
 */
export const processImageUrl = ( path: string, options: ImageUrlOpts = {} ): string => {
    const fullUrl = getBaseImageUrl( path, options );
    if ( !fullUrl ) {
        return "";
    }

    if ( options.noResize || !isResizableImage( path ) ) {
        return fullUrl;
    }

    if ( !isProjectOssUrl( fullUrl, options ) ) {
        return fullUrl;
    }

    const processParams = buildResizeParams( options );
    return appendResizeParams( fullUrl, processParams );
};
